import {
  type MappingCount,
  useOrderDashboardMappingMetrics,
} from '@/api/hooks/useOrderDashboard'
import { useObject } from '@/contexts/ObjectContext'
import { formatCount } from '@/utils/stringFormatters'
import {
  Horizontal,
  Texto,
  Vertical,
  useThemeContext,
} from '@gravitate-js/excalibrr'
import { Card, Progress, Segmented, Tooltip } from 'antd'
import { useState } from 'react'
import { Link } from 'react-router-dom'

// `?object=` is how the Reference Data panel picks which grid to open (see
// ReferenceDataPanel), and ObjectContext's `value` is what it matches on.
const REFERENCE_DATA_PATH = '/integration-management/reference-data'

/**
 * Which of the two coverage rates the card shows. "Requested" only counts
 * records a Crossroads integration has actually asked to have mapped -- the
 * backend calls that set `needs_mapping` -- while "All Data" counts
 * everything the tenant has. Both rates are computed server side.
 */
type CoverageView = 'requested' | 'all'

interface Coverage {
  percentage: number
  /** What the rate is out of; zero means there is nothing to map yet. */
  denominator: number
}

/**
 * Dial colours, brighter than the --theme-* tokens the tables use. A dial is
 * a solid ring of colour rather than a few characters of text, so it reads as
 * heavy-handed at the tokens' saturation -- the same reason the audit modal
 * keeps its own palette.
 */
const DIAL_COLORS = {
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  empty: '#bfbfbf',
} as const

function coverageFor(
  count: MappingCount | undefined,
  view: CoverageView,
): Coverage {
  if (!count) return { percentage: 0, denominator: 0 }
  return view === 'requested'
    ? {
        percentage: count.needs_mapping_percentage,
        denominator: count.needs_mapping_total,
      }
    : { percentage: count.all_percentage, denominator: count.total }
}

/**
 * Coverage bands for reference data, which is held to a looser standard than
 * the event rates above -- unmapped records are a backlog, not a failure.
 */
function coverageColor({ percentage, denominator }: Coverage): string {
  if (denominator === 0) return DIAL_COLORS.empty
  if (percentage >= 95) return DIAL_COLORS.success
  if (percentage >= 80) return DIAL_COLORS.warning
  return DIAL_COLORS.error
}

interface CoverageDialProps {
  label: string
  /** ObjectContext's value for the type, which is also its `?object=` param. */
  object: string
  count: MappingCount | undefined
  view: CoverageView
  trailColor: string
}

function CoverageBreakdown({ count }: { count: MappingCount | undefined }) {
  if (!count) return <>No reference data of this type</>
  return (
    <>
      <div>{`${formatCount(count.mapped)} Mapped`}</div>
      <div>
        {`${formatCount(count.needs_mapping)} ${
          count.needs_mapping === 1 ? 'Needs' : 'Need'
        } Mapping`}
      </div>
    </>
  )
}

function CoverageDial({
  label,
  object,
  count,
  view,
  trailColor,
}: CoverageDialProps) {
  const coverage = coverageFor(count, view)

  return (
    <Link
      to={`${REFERENCE_DATA_PATH}?object=${object}`}
      className='transition-opacity hover:opacity-75'
      style={{ display: 'block', flex: 1, minWidth: 0, color: 'inherit' }}
    >
      <Vertical gap={8} horizontalCenter>
        <Tooltip title={<CoverageBreakdown count={count} />}>
          {/* A span between the two: antd's Tooltip needs a child it can hand
              a ref and its own props to, which Progress is not. */}
          <span style={{ display: 'inline-flex' }}>
            <Progress
              type='circle'
              size={96}
              strokeWidth={10}
              strokeLinecap='butt'
              percent={coverage.percentage}
              strokeColor={coverageColor(coverage)}
              trailColor={trailColor}
              format={(percent) => (
                // A plain span: antd centres .ant-progress-text itself, and a
                // block-level child (what Texto renders) knocks the label off
                // centre and clips it against the ring.
                <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}>
                  {`${percent ?? 0}%`}
                </span>
              )}
            />
          </span>
        </Tooltip>
        <Texto style={{ fontSize: 12 }}>{label}</Texto>
      </Vertical>
    </Link>
  )
}

/**
 * Mapping coverage per reference-data type. The types, their labels and their
 * order all come from the same list the Reference Data page's own filter uses,
 * so the two pages never disagree about what a tenant has -- and each dial
 * deep-links into that page's grid for its own type.
 */
export function ReferenceDataCard() {
  const [view, setView] = useState<CoverageView>('requested')
  const { availableObjects } = useObject()
  const { isDarkTheme } = useThemeContext()
  const { data, isLoading } = useOrderDashboardMappingMetrics()

  // The unfilled part of a ring has to read as a groove against the card
  // rather than as another value, so it stays a low-contrast neutral.
  const trailColor = isDarkTheme ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.07)'

  return (
    <Card
      size='small'
      loading={isLoading}
      title={
        <Texto
          appearance='hint'
          weight={600}
          textTransform='uppercase'
          style={{ fontSize: 11, letterSpacing: 0.5 }}
        >
          Reference Data
        </Texto>
      }
      extra={
        <Segmented<CoverageView>
          size='small'
          value={view}
          onChange={setView}
          options={[
            { value: 'requested', label: 'Requested' },
            { value: 'all', label: 'All Data' },
          ]}
        />
      }
    >
      <Horizontal gap={8}>
        {availableObjects.map((object) => (
          <CoverageDial
            key={object.value}
            label={object.label}
            object={object.value}
            count={data?.[object.mapping_type]}
            view={view}
            trailColor={trailColor}
          />
        ))}
      </Horizontal>
    </Card>
  )
}
